import { IListaTermoNotificacao } from "@/interfaces/IListaTermoNotificacao";
import { IParecerTermoNotificacao } from "@/interfaces/IParecerTermoNotificacao";
import { ITermoNotificacao } from "@/interfaces/ITermoNotificacao";
import { api } from "@/services/api";
import { termoNotificacaoAction } from "@/store/actions/termoNotificacao";
import { getTermosNotificacao } from "@/store/reducers/termoNotificacao";
import { useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";

export const useTermoNotificacao = () => {
  const { user } = useAuth();
  const appDispatch = useDispatch();
  const { add, remove, update } = termoNotificacaoAction;
  const listaTermosNotificacao = useSelector(getTermosNotificacao);

  const addTermoNotificacao = (item: ITermoNotificacao) => {
    appDispatch(add(item));
  };

  const removeTermoNotificacao = (offlineId: number) => {
    appDispatch(remove(offlineId));
  };

  const updateTermoNotificacao = (item: ITermoNotificacao) => {
    appDispatch(update(item));
  };

  const getTermosNotificacaoByOfflineId = (offlineId: number) => {
    return listaTermosNotificacao.find(
      (termo) => termo.offlineId === offlineId
    );
  };

  const submitTermoNotificacaoApi = async (
    item: Omit<ITermoNotificacao, "id">
  ) => {
    const { data } = await api.post<ITermoNotificacao>(
      "/termos-notificacao",
      item,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      }
    );
    return data;
  };

  const cancelarTermoNotificacaoApi = async (id: number, motivo: string) => {
    await api.patch(
      `/termos-notificacao/${id}/cancelar`,
      { motivo },
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
  };

  const getTermoNotificacaoByIdApi = async (id: number) => {
    const { data } = await api.get<ITermoNotificacao>(
      `/termos-notificacao/${id}`,
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
    return data;
  };

  const getTermosNotificacaoApi = async (params: {
    page: number;
    estabelecimentoId: number;
    dataCadastro?: string;
    numero?: string;
    status?: string;
  }) => {
    const { data } = await api.get<IListaTermoNotificacao>(
      `/termos-notificacao`,
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
        params: {
          ...params,
          size: 20,
          page: params.page - 1,
        },
      }
    );
    return data;
  };

  const submitSaneamentoTermoNotificacaoApi = async (
    termoId: number,
    formData: FormData
  ) => {
    const { data } = await api.post<ITermoNotificacao>(
      `/termos-notificacao/${termoId}/saneamentos`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  };

  const submitParecerTermoNotificacaoApi = async (
    termoId: number,
    saneamentoId: number,
    parecer: IParecerTermoNotificacao
  ) => {
    const { data } = await api.patch<ITermoNotificacao>(
      `/termos-notificacao/${termoId}/saneamentos/${saneamentoId}/parecer`,
      parecer,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      }
    );
    return data;
  };

  const getTermoNotificacaoSaneamentoApi = async (termoId: number) => {
    const { data } = await api.get<ITermoNotificacao>(
      `/termos-notificacao/${termoId}/saneamentos`,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      }
    );
    return data;
  };

  const getAnexoApi = async (nomeUpload: string) => {
    try {
      const { data } = await api.get<Blob>(
        `/termos-notificacao/anexo/${nomeUpload}`,
        {
          headers: { Authorization: `Bearer ${user?.access_token}` },
          responseType: "blob",
        }
      );
      return data;
    } catch (error) {
      const axiosError = error as any;
      if (error instanceof Error && "response" in error) {
        if (axiosError.response?.data instanceof Blob) {
          const text = await axiosError.response.data.text();
          try {
            const jsonError = JSON.parse(text);
            axiosError.response.data = jsonError;
          } catch {
            axiosError.response.data = { detail: text };
          }
        }
      }

      throw error;
    }
  };

  return {
    listaTermosNotificacao,
    addTermoNotificacao,
    removeTermoNotificacao,
    updateTermoNotificacao,
    getTermosNotificacaoByOfflineId,
    submitTermoNotificacaoApi,
    cancelarTermoNotificacaoApi,
    getTermoNotificacaoByIdApi,
    getTermosNotificacaoApi,
    submitSaneamentoTermoNotificacaoApi,
    submitParecerTermoNotificacaoApi,
    getTermoNotificacaoSaneamentoApi,
    getAnexoApi,
  };
};
